import React, { useState, useEffect, createContext } from "react";
import {
  createBrowserRouter,
  RouterProvider,
  createRoutesFromElements,
  Route,
  Navigate,
  useNavigate,
  Outlet,
  useLocation,
} from "react-router-dom";
import { Provider, useDispatch, useSelector } from "react-redux";
import { ThemeProvider } from "next-themes";
import store from "./store";
import { bootstrap, toggleSidebar, logoutUser, fetchWorkspaces } from "./store";
import { cn } from "./lib/utils";
import axios from "./api/api";

// Components
import Sidebar from "./components/Sidebar";
import Navbar from "./components/Navbar";
import Dashboard from "./components/Dashboard";
import Login from "./components/Login";
import Register from "./components/Register";
import ForgotPassword from "./components/ForgotPassword";
import QueueEmails from "./components/QueueEmails";
import ProcessingEmails from "./components/ProcessingEmails";
import NotSendedEmails from "./components/NotSendedEmails";
import CsvUpload from "./components/CsvUpload";
import BatchSend from "./components/BatchSend";
import Settings from "./components/Settings";
import Inbox from "./components/Inbox";
import GlobalContacts from "./components/GlobalContacts";
import InboxAutomations from "./components/InboxAutomations";
import AutomationBuilder from "./components/AutomationBuilder";
import Contacts from "./components/Contacts";
import ContactDetail from "./components/ContactDetail";
import CrmCompanies from "./components/CrmCompanies";
import CrmLists from "./components/CrmLists";
import CrmListDetail from "./components/CrmListDetail";
import EmailFinder from "./components/EmailFinder";
import SavedEmails from "./components/SavedEmails";
import VerifiedEmails from "./components/VerifiedEmails";
import Workflows from "./components/Workflows";
import Approvals from "./components/Approvals";
import Opportunities from "./components/Opportunities";
import OpportunityDetail from "./components/OpportunityDetail";
import OpportunityForm from "./components/OpportunityForm";
import Tasks from "./components/Tasks";
import SalesDashboard from "./components/SalesDashboard";
import PlanBilling from "./components/PlanBilling";
import VaultWebhooks from "./components/VaultWebhooks";
import Suppressions from "./components/Suppressions";
import Sequences from "./components/Sequences";
import Templates from "./components/Templates";
import Groups from "./components/Groups";
import GroupDetail from "./components/GroupDetail";
import MailAccounts from "./components/MailAccounts";
import SenderPools from "./components/SenderPools";
import Campaigns from "./components/Campaigns";
import CampaignDetail from "./components/CampaignDetail";
import OpsDashboard from "./components/OpsDashboard";
import Reports from "./components/Reports";
import Sessions from "./components/Sessions";
import CustomerSuccess from "./components/CustomerSuccess";
import SignalGovernanceDashboard from "./components/SignalGovernanceDashboard";
import Documentation from "./components/Documentation";
import ExternalCheckoutSimulator from "./components/ExternalCheckoutSimulator";
import { ConfirmProvider } from "./contexts/ConfirmContext";
import { CreditsProvider } from "./contexts/CreditsContext";
import {
  EntitlementsProvider,
  useEntitlements,
} from "./contexts/EntitlementsContext";
import {
  EntitlementBlockerModalProvider,
  useEntitlementBlockerModal,
} from "./contexts/EntitlementBlockerModalContext";
import LoadingScreen from "./components/ui/LoadingScreen";
import { Alert, AlertDescription, AlertTitle } from "./components/ui/alert";
import { X } from "lucide-react";

// Forensic View lazy-load
const RunCompareView = React.lazy(() => import("./components/RunCompareView"));

export const SnackbarContext = createContext();

const PrivateRoute = ({ children }) => {
  const { isAuthenticated, loading } = useSelector((state) => state.auth);
  if (loading) return null;
  return isAuthenticated ? children : <Navigate to="/login" />;
};

const EntitledRoute = ({ featureKey, children }) => {
  const navigate = useNavigate();
  const { hasEntitlement, isLoading, planCode, snapshot } = useEntitlements();
  const { openBlockerModal } = useEntitlementBlockerModal();
  const [hasBlockedOnce, setHasBlockedOnce] = useState(false);

  const isReady = !!snapshot;

  useEffect(() => {
    if (isLoading || !isReady || hasBlockedOnce) return;
    if (hasEntitlement(featureKey)) return;

    console.log("islocked test deneme")
    openBlockerModal({
      blockerCategory: "FEATURE_LOCKED",
      details: { featureKey, planCode },
      message: "This page is not available on your current plan.",
      context: { surface: "route-guard", featureKey },
    });
    setHasBlockedOnce(true);
    navigate("/dashboard", { replace: true });
  }, [
    featureKey,
    hasBlockedOnce,
    hasEntitlement,
    isLoading,
    isReady,
    navigate,
    openBlockerModal,
    planCode,
  ]);

  if (isLoading || !isReady) return <LoadingScreen />;
  if (!hasEntitlement(featureKey)) return null;
  return children;
};

function MainLayoutContent() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isBootstrapped } = useSelector(
    (state) => state.auth,
  );
  const { isCollapsed } = useSelector((state) => state.ui);
  const { showEntitlementBlocker } = useEntitlementBlockerModal();

  useEffect(() => {
    dispatch(bootstrap());
  }, [dispatch]);

  useEffect(() => {
    if (isAuthenticated && isBootstrapped) {
      dispatch(fetchWorkspaces());
    }
  }, [isAuthenticated, isBootstrapped, dispatch]);

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const statusCode = error?.response?.status;
        if ([400, 403, 422].includes(statusCode)) {
          showEntitlementBlocker(error, {
            context: { source: "global-interceptor" },
          });
        }
        return Promise.reject(error);
      },
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [showEntitlementBlocker]);

  if (!isBootstrapped) {
    return <LoadingScreen />;
  }

  const isAuthPage =
    location.pathname === "/login" || location.pathname === "/register" || location.pathname === "/forgot-password";
  const isDiscoveryPage = location.pathname === "/discovery";

  const handleLogout = async () => {
    await dispatch(logoutUser());
    navigate("/login");
  };

  return (
    <div className="flex h-screen bg-background selection:bg-primary/10">
      {isAuthenticated && <Sidebar />}

      <div
        className={cn(
          "flex-1 flex flex-col h-screen transition-all duration-500 ease-in-out",
          isAuthenticated &&
          (isCollapsed ? "lg:ml-[68px]" : "lg:ml-[300px] xl:ml-[320px]"),
        )}
      >
        {isAuthenticated && (
          <Navbar
            onMenuToggle={() => dispatch(toggleSidebar())}
            onLogout={handleLogout}
          />
        )}

        <main
          className={cn(
            "flex-1 min-h-0 transition-all duration-500",
            isAuthenticated ? "pt-16" : "pt-0",
          )}
        >
          <div
            className={cn(
              "min-h-0",
              isAuthPage ? "h-full overflow-hidden p-0" : "",
              isDiscoveryPage ? "h-[calc(100vh-4rem)] overflow-hidden p-0" : "",
              !isAuthPage && !isDiscoveryPage ? "h-full overflow-auto p-4 sm:p-8 lg:p-10" : "",
              isAuthenticated && !isDiscoveryPage
                ? "bg-muted/50  border-t border-l border-border/50 shadow-inner"
                : "",
              isAuthenticated && isDiscoveryPage ? "bg-background" : ""
            )}
          >
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

function MainLayout() {
  return (
    <EntitlementBlockerModalProvider>
      <MainLayoutContent />
    </EntitlementBlockerModalProvider>
  );
}

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<MainLayout />}>
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route
        path="/dashboard"
        element={
          <PrivateRoute>
            <Dashboard />
          </PrivateRoute>
        }
      />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route
        path="/queue"
        element={
          <PrivateRoute>
            <QueueEmails />
          </PrivateRoute>
        }
      />
      <Route
        path="/processing"
        element={
          <PrivateRoute>
            <ProcessingEmails />
          </PrivateRoute>
        }
      />
      <Route
        path="/not-sended"
        element={
          <PrivateRoute>
            <NotSendedEmails />
          </PrivateRoute>
        }
      />
      <Route path="/documentation" element={<Documentation />} />
      <Route
        path="/upload"
        element={
          <PrivateRoute>
            <CsvUpload />
          </PrivateRoute>
        }
      />
      <Route
        path="/batch"
        element={
          <PrivateRoute>
            <BatchSend />
          </PrivateRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <PrivateRoute>
            <Settings />
          </PrivateRoute>
        }
      />
      <Route
        path="/inbox"
        element={
          <PrivateRoute>
            <Inbox />
          </PrivateRoute>
        }
      />
      <Route
        path="/discovery"
        element={
          <PrivateRoute>
            <GlobalContacts />
          </PrivateRoute>
        }
      />
      <Route
        path="/crm/companies"
        element={
          <PrivateRoute>
            <CrmCompanies />
          </PrivateRoute>
        }
      />
      <Route
        path="/crm/contacts"
        element={
          <PrivateRoute>
            <Contacts />
          </PrivateRoute>
        }
      />
      <Route
        path="/crm/contacts/:id"
        element={
          <PrivateRoute>
            <ContactDetail />
          </PrivateRoute>
        }
      />
      <Route
        path="/crm/lists"
        element={
          <PrivateRoute>
            <CrmLists />
          </PrivateRoute>
        }
      />
      <Route
        path="/crm/lists/:id"
        element={
          <PrivateRoute>
            <CrmListDetail />
          </PrivateRoute>
        }
      />
      <Route
        path="/email-finder"
        element={
          <PrivateRoute>
            <EmailFinder />
          </PrivateRoute>
        }
      />
      <Route
        path="/verified-emails"
        element={
          <PrivateRoute>
            <VerifiedEmails />
          </PrivateRoute>
        }
      />
      <Route
        path="/saved-emails"
        element={
          <PrivateRoute>
            <SavedEmails />
          </PrivateRoute>
        }
      />
      <Route
        path="/approvals"
        element={
          <PrivateRoute>
            <EntitledRoute featureKey="canUseAdvancedGovernance">
              <Approvals />
            </EntitledRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/workflows/runs/:runId/compare/:historyId"
        element={
          <PrivateRoute>
            <EntitledRoute featureKey="canUseWorkflows">
              <React.Suspense fallback={<LoadingScreen />}>
                <RunCompareView />
              </React.Suspense>
            </EntitledRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/workflows/:id/runs/:runId"
        element={
          <PrivateRoute>
            <EntitledRoute featureKey="canUseWorkflows">
              <Workflows />
            </EntitledRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/workflows/:id/runs"
        element={
          <PrivateRoute>
            <EntitledRoute featureKey="canUseWorkflows">
              <Workflows />
            </EntitledRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/workflows/:id"
        element={
          <PrivateRoute>
            <EntitledRoute featureKey="canUseWorkflows">
              <Workflows />
            </EntitledRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/workflows/runs/:runId"
        element={
          <PrivateRoute>
            <EntitledRoute featureKey="canUseWorkflows">
              <Workflows />
            </EntitledRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/workflows/runs"
        element={
          <PrivateRoute>
            <EntitledRoute featureKey="canUseWorkflows">
              <Workflows />
            </EntitledRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/workflows"
        element={
          <PrivateRoute>
            <EntitledRoute featureKey="canUseWorkflows">
              <Workflows />
            </EntitledRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/inbox/automations"
        element={
          <PrivateRoute>
            <InboxAutomations />
          </PrivateRoute>
        }
      />
      <Route
        path="/inbox/automations/:id/builder"
        element={
          <PrivateRoute>
            <AutomationBuilder />
          </PrivateRoute>
        }
      />
      <Route
        path="/opportunities"
        element={
          <PrivateRoute>
            <Opportunities />
          </PrivateRoute>
        }
      />
      <Route
        path="/opportunities/new"
        element={
          <PrivateRoute>
            <OpportunityForm />
          </PrivateRoute>
        }
      />
      <Route
        path="/opportunities/:id"
        element={
          <PrivateRoute>
            <OpportunityDetail />
          </PrivateRoute>
        }
      />
      <Route
        path="/opportunities/:id/edit"
        element={
          <PrivateRoute>
            <OpportunityForm />
          </PrivateRoute>
        }
      />
      <Route
        path="/tasks"
        element={
          <PrivateRoute>
            <EntitledRoute featureKey="canUseTasking">
              <Tasks />
            </EntitledRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/sales-dashboard"
        element={
          <PrivateRoute>
            <EntitledRoute featureKey="canUseSalesDashboard">
              <SalesDashboard />
            </EntitledRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/plan"
        element={
          <PrivateRoute>
            <PlanBilling />
          </PrivateRoute>
        }
      />
      <Route
        path="/vault"
        element={
          <PrivateRoute>
            <EntitledRoute featureKey="canUseWebhooks">
              <VaultWebhooks />
            </EntitledRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/suppressions"
        element={
          <PrivateRoute>
            <Suppressions />
          </PrivateRoute>
        }
      />
      <Route
        path="/sequences/*"
        element={
          <PrivateRoute>
            <EntitledRoute featureKey="canUseSequences">
              <Sequences />
            </EntitledRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/templates/*"
        element={
          <PrivateRoute>
            <Templates />
          </PrivateRoute>
        }
      />
      <Route
        path="/mail-accounts"
        element={
          <PrivateRoute>
            <MailAccounts />
          </PrivateRoute>
        }
      />
      <Route
        path="/sender-pools"
        element={
          <PrivateRoute>
            <SenderPools />
          </PrivateRoute>
        }
      />
      <Route
        path="/campaigns"
        element={
          <PrivateRoute>
            <Campaigns />
          </PrivateRoute>
        }
      />
      <Route
        path="/campaigns/:id"
        element={
          <PrivateRoute>
            <CampaignDetail />
          </PrivateRoute>
        }
      />
      <Route
        path="/groups"
        element={
          <PrivateRoute>
            <Groups />
          </PrivateRoute>
        }
      />
      <Route
        path="/groups/:id"
        element={
          <PrivateRoute>
            <GroupDetail />
          </PrivateRoute>
        }
      />
      <Route
        path="/ops"
        element={
          <PrivateRoute>
            <OpsDashboard />
          </PrivateRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <PrivateRoute>
            <EntitledRoute featureKey="canUseAdvancedReporting">
              <Reports />
            </EntitledRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/sessions"
        element={
          <PrivateRoute>
            <Sessions />
          </PrivateRoute>
        }
      />
      <Route
        path="/success"
        element={
          <PrivateRoute>
            <CustomerSuccess />
          </PrivateRoute>
        }
      />
      <Route
        path="/ops/signals"
        element={
          <PrivateRoute>
            <EntitledRoute featureKey="canUseAdvancedGovernance">
              <SignalGovernanceDashboard />
            </EntitledRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/external-checkout"
        element={<ExternalCheckoutSimulator />}
      />
      <Route path="*" element={<Navigate to="/dashboard" />} />
    </Route>,
  ),
);

function App() {
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "default",
  });

  const showSnackbar = (message, severity = "default") => {
    setSnackbar({ open: true, message, severity });
    setTimeout(() => setSnackbar((prev) => ({ ...prev, open: false })), 3000);
  };

  return (
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
      <Provider store={store}>
        <div className="min-h-screen bg-background font-sans antialiased text-foreground">
          <EntitlementsProvider>
            <CreditsProvider>
              <ConfirmProvider>
                <SnackbarContext.Provider value={showSnackbar}>
                  <RouterProvider router={router} />

                  {snackbar.open && (
                    <div className="fixed top-20 right-6 z-[2000] w-full max-w-sm animate-in slide-in-from-right duration-300">
                      <Alert
                        variant={
                          snackbar.severity === "error"
                            ? "destructive"
                            : "default"
                        }
                        className="shadow-2xl bg-card/95 backdrop-blur-md text-card-foreground border-border/50 rounded-2xl p-4 flex justify-between items-start gap-3"
                      >
                        <div className="grid gap-1">
                          {snackbar.severity === "error" && (
                            <AlertTitle className="text-xs font-black uppercase tracking-widest opacity-70">
                              Error
                            </AlertTitle>
                          )}
                          <AlertDescription className="text-sm font-medium">
                            {snackbar.message}
                          </AlertDescription>
                        </div>
                        <button
                          onClick={() =>
                            setSnackbar((prev) => ({ ...prev, open: false }))
                          }
                          className="text-muted-foreground hover:text-foreground transition-colors p-1"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </Alert>
                    </div>
                  )}
                </SnackbarContext.Provider>
              </ConfirmProvider>
            </CreditsProvider>
          </EntitlementsProvider>
        </div>
      </Provider>
    </ThemeProvider>
  );
}

export default App;
